import type {
  FindFilterCountsInput,
  FindProductsInput,
} from '@travel-planner/shared/schemas';
import type { ProductsSearch } from './search-params';
import { visibilityToWithHidden } from '../lib/visibility';

// CMS / Mini Planner visibility filter → `isHiddenOn*`. visible → false,
// hidden → true, undefined (All) → omit.
const cmsVisibilityToHidden = (
  v: ProductsSearch['cmsVisibility' | 'miniPlannerVisibility']
): boolean | undefined =>
  v === 'visible' ? false : v === 'hidden' ? true : undefined;

const DEFAULT_PAGE_SIZE = 20;

// 4-corner viewport bbox from the URL search — only when all corners are
// present (a partial bbox is treated as "no viewport filter").
export const bboxFromSearch = (
  s: ProductsSearch
): FindProductsInput['bbox'] | undefined =>
  typeof s.neLat === 'number' &&
  typeof s.neLng === 'number' &&
  typeof s.swLat === 'number' &&
  typeof s.swLng === 'number'
    ? {
        northEastLat: s.neLat,
        northEastLng: s.neLng,
        southWestLat: s.swLat,
        southWestLng: s.swLng,
      }
    : undefined;

// Maps the URL search into the shared products query input. Both the table
// and the 50/50 map build their query through this so they hit the SAME
// React Query key (one request, mirrored results). `title` lets the table
// pass its debounced value; the map mirrors the raw URL term.
export const toProductsQueryInput = (
  s: ProductsSearch,
  title: string | undefined = s.search || undefined
): FindProductsInput => {
  const page = s.page ?? 1;
  const pageSize = s.pageSize ?? DEFAULT_PAGE_SIZE;
  return {
    offset: (page - 1) * pageSize,
    limit: pageSize,
    title,
    city: s.city || undefined,
    supplierName: s.supplierName || undefined,
    contentCategory: s.contentCategory,
    contentSubcategory: s.contentSubcategory,
    catalogId: s.catalogId,
    country: s.country,
    valueTier: s.valueTier,
    sortBy: s.sortBy,
    sortOrder: s.sortOrder,
    // Schema field is `isHidden` (true = hidden-only, false = visible-only,
    // undefined = both) — matches the helper's boolean semantics.
    isHidden: visibilityToWithHidden(s.visibility),
    isHiddenOnCms: cmsVisibilityToHidden(s.cmsVisibility),
    isHiddenOnMiniPlanner: cmsVisibilityToHidden(s.miniPlannerVisibility),
    customRegionIds: s.regionId ? [s.regionId] : undefined,
    bbox: bboxFromSearch(s),
  };
};

// Maps the URL search into the `products.filterCounts` input. That endpoint
// accepts only a narrow subset of the list filters (catalogId | country |
// bbox | valueTier — NOT category/subcategory/search/region), and each
// dimension's count excludes its own filter so the dropdowns can stay
// dynamic (selecting a country narrows the category counts, etc.).
export const toFilterCountsInput = (
  s: ProductsSearch
): FindFilterCountsInput => ({
  catalogId: s.catalogId,
  country: s.country,
  valueTier: s.valueTier,
  isHiddenOnCms: cmsVisibilityToHidden(s.cmsVisibility),
  bbox: bboxFromSearch(s),
});
